import React, { useState, useEffect, Suspense, lazy } from 'react';
import includes from 'lodash/includes';
import isEmpty from 'lodash/isEmpty';
import classNames from 'classnames';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faLock, faMagnifyingGlass } from '@fortawesome/free-solid-svg-icons';
import { FormattedMessage } from 'react-intl';
import { Container, Navbar, Nav, NavItem, NavLink } from 'reactstrap';
import { useSelector } from 'react-redux';

import env from 'Env';
import { countryCode, countryName, isBanned } from 'Helpers/Geo/Country';
import AutoSearchWrapper from 'Layout/NavBar/AutoSearch/AutoSearchWrapper';
import BrandAcer from 'Layout/NavBar/Brands/BrandAcer';
import BrandBox from 'Layout/NavBar/Brands/BrandBox';
import BrandBundlestars from 'Layout/NavBar/Brands/BrandBundlestars';
import BrandCcl from 'Layout/NavBar/Brands/BrandCcl';
import BrandChilliblast from 'Layout/NavBar/Brands/BrandChilliblast';
import BrandFanatical from 'Layout/NavBar/Brands/BrandFanatical';
import BrandGamespot from 'Layout/NavBar/Brands/BrandGamespot';
import BrandHpOmen from 'Layout/NavBar/Brands/BrandHpOmen';
import BrandInsidetech from 'Layout/NavBar/Brands/BrandInsidetech';
import BrandPowerColor from 'Layout/NavBar/Brands/BrandPowerColor';
import BrandRazer from 'Layout/NavBar/Brands/BrandRazer';
import BrandStormforce from 'Layout/NavBar/Brands/BrandStormforce';
import NavCartButton from 'Layout/NavBar/NavCartButton';
import CheckoutSideBar from 'Layout/SideBar/CheckoutSideBar';
import GeneralUtils from 'Helpers/GeneralUtils';
import getUrlParameter from 'Helpers/Url/GetUrlParameter';
import GoogleOneTap from 'Layout/NavBar/GoogleOneTap';
import isBot from 'Helpers/SEO/is-bot';
import { isCompetitionPagePath } from 'Helpers/Acquisition/competition-paths';
import LocalisationPrompt from 'Layout/NavBar/LocalisationPrompt';
import LoginBtn from 'Layout/NavBar/LoginBtn';
import SecondaryNavBar from 'Layout/NavBar/SecondaryNavBar';
import SlimBannerContainer from 'Components/Banner/SlimBanner/SlimBannerContainer';
import store from 'Redux/store';
import UspBar from 'Components/InfoBar/Usp/UspBar';
import WishlistNavButton from 'Layout/NavBar/WishlistNavButton';
import useViewport from 'Hooks/useViewport';
import useDeferredMegaMenu from 'Hooks/useDeferredMegaMenu';
import MobileSideBar from 'Layout/NavBar/MobileNav/MobileSideBar';
import EmailSubscribeBar from 'Layout/NavBar/EmailSubscribeBar';
import useEmailSubscribeStatus from 'Hooks/Acquisition/useEmailSubscribeStatus';
import { subscribedWithinWindow, NAV_SUBSCRIBED_DATE } from 'Helpers/CRM/subscribe-suppression';

const MegaMenusContainer = lazy(() => import(/* webpackChunkName: "MegaMenus" */ 'Layout/NavBar/MegaMenu/MegaMenusContainer'));

export default function NavBar() {
  const authenticated = useSelector(state => state.auth?.authenticated) || false;
  const isAdminLoggedInAs = useSelector(state => state.auth?.isAdminLoggedInAs) || '';
  const email = useSelector(state => state.auth?.email) || '';
  const location = useSelector(state => state.router?.location);
  const pathname = useSelector(state => state.router?.location.pathname) || '';
  const flashSale = useSelector(state => state.flashSale) || {
    upcomingDeals: [],
    currentDeals: [],
    nextDeal: 0,
  };
  const siteBrand = useSelector(state => state.siteBrand) || 'fanatical';
  const notificationCentreOpen = useSelector(state => state.notifications?.centreOpen) || false;

  const [showWelcomeBack, setShowWelcomeBack] = useState(false);
  const [showMobile, setShowMobile] = useState(false);
  const [megaMenuTab, setMegaMenuTab] = useState('');
  const [shouldLoadMegaMenu, loadMegaMenu] = useDeferredMegaMenu();
  const userInCart = pathname === '/cart';
  const isHomePage = pathname === '/';

  useEffect(() => {
    if (!isBot()) {
      const lastSeenWelcomeBack = localStorage.getItem('lastSeenWelcomeBack');
      // const twentyFourHours = 10000; // 10 Seconds
      const twentyFourHours = 86400000;

      const pageDisabled = includes(window.location.href, '/cart') ||
        includes(window.location.href, '/receipt') ||
        includes(window.location.href, '/payment') ||
        includes(window.location.href, '/steam') ||
        !!getUrlParameter('v') ||
        getUrlParameter('add_to_basket');

      if (
        (!pageDisabled &&
          (!lastSeenWelcomeBack ||
            (lastSeenWelcomeBack && (Date.now() - lastSeenWelcomeBack) > twentyFourHours)))
        || getUrlParameter('always_show_checkout_bar')
      ) {
        setTimeout(() => {
          const reduxStore = store.getState();
          if (!isEmpty(reduxStore.cart.items)) {
            setShowWelcomeBack(true);
            localStorage.setItem('lastSeenWelcomeBack', Date.now());
          }
          // Waiting for cart items
        }, 1800);
      }
    }
  }, []);

  const hideNav = location.pathname && ((location.pathname.includes('/partners/hp-omen') && !location.pathname.includes('/partners/hp-omen-gaming-hub')) || location.pathname.includes('/welcome-new-user'));

  const newUser = GeneralUtils.newUser();
  const googleOneTapUser = localStorage.getItem('isMobileGoogleOneTapLoginUser');
  const dismissedOneTap = localStorage.getItem('dismissedOneTap');
  const { canSubscribe } = useEmailSubscribeStatus();
  const navSubscribedLast24hours = subscribedWithinWindow(NAV_SUBSCRIBED_DATE);

  const toggleSearch = () => setShowMobile(!showMobile);

  const { isBelowLg, isAboveMd } = useViewport();

  const showSecureCartTitle = userInCart && isBelowLg;

  const cleanNav = false; // logic around this will return in a follow cart test

  const isCompetitionProductPage = isCompetitionPagePath(location.pathname);

  const clearNav =
    ['/showcase', '/redeem-code', '/sidemen'].some(path =>
      location.pathname.includes(path),
    ) || isCompetitionProductPage;

  return (
    <React.Fragment>
      {isAdminLoggedInAs &&
        <div className="admin-warning-bar">
          Warning - Admin Logged in as: {email}
        </div>
      }
      <div className={`${hideNav ? 'd-none' : ''}`}>
        {showWelcomeBack &&
          <CheckoutSideBar />
        }
        <LocalisationPrompt />

        {siteBrand === 'fanatical' && !clearNav &&
          <div>
            {(canSubscribe && isHomePage) && !navSubscribedLast24hours ?
              <EmailSubscribeBar
                mobileEnabled={isBelowLg}
                appearsOn="home_usp"
              /> :
              <UspBar mobileEnabled={isBelowLg} />
            }
          </div>
        }

        <Navbar expand="md" className={classNames('NavBar', { 'NavBar--clear': clearNav })}>
          <div className="w-100">
            <Container className="nav-container top-navbar">

              <div className="logo-container">
                {siteBrand === 'fanatical' &&
                  <BrandFanatical />
                }
                {siteBrand === 'bundlestars' &&
                  <BrandBundlestars />
                }
                {siteBrand === 'hpomen' &&
                  <BrandHpOmen />
                }
                {siteBrand === 'acer' &&
                  <BrandAcer />
                }
                {siteBrand === 'gamespot' &&
                  <BrandGamespot />
                }
                {siteBrand === 'chilliblast' &&
                  <BrandChilliblast />
                }
                {siteBrand === 'powercolor' &&
                  <BrandPowerColor />
                }
                {siteBrand === 'ccl' &&
                  <BrandCcl />
                }
                {siteBrand === 'box' &&
                  <BrandBox />
                }
                {siteBrand === 'razer' &&
                  <BrandRazer />
                }
                {siteBrand === 'insidetech' &&
                  <BrandInsidetech />
                }
                {siteBrand === 'stormforce' &&
                  <BrandStormforce />
                }

                <div className="tablet-search">
                  {showSecureCartTitle &&
                    <h1 className="checkout-title h5">
                      <FontAwesomeIcon icon={faLock} className="lock-icon" />
                      <FormattedMessage id="navbar.your-cart" defaultMessage="Your Secure Cart" />
                    </h1>
                  }

                  {(!showSecureCartTitle && !cleanNav) &&
                    <React.Fragment>
                      <div className="d-none d-md-flex w-100">
                        <AutoSearchWrapper />
                      </div>
                      <div className="mobile-search-btn-container mobile-cart-items">
                        <NavLink
                          className="d-flex align-items-center"
                          onClick={toggleSearch}
                        >
                          <FontAwesomeIcon icon={faMagnifyingGlass} />
                        </NavLink>
                      </div>
                    </React.Fragment>
                  }

                  {(!showSecureCartTitle && !cleanNav) &&
                    <React.Fragment>
                      <div className="mobile-cart-items">
                        <NavCartButton />
                      </div>
                      <MobileSideBar />

                    </React.Fragment>
                  }
                </div>

              </div>
              {!cleanNav &&
                <div className="d-none d-lg-block autosearch-nav navbar-nav">
                  <AutoSearchWrapper />
                </div>
              }
              {!cleanNav &&
                <Nav navbar className="login-cart-container d-none d-lg-flex">
                  <NavItem className="pr-0">
                    <div className="nav-top-links pr-2">
                      <LoginBtn className="sign-in-btn" />
                    </div>

                    <div className="cart-items">
                      <WishlistNavButton />
                    </div>
                    <div className="cart-items">
                      <NavCartButton showPrice />
                    </div>
                  </NavItem>
                </Nav>
              }


              {!authenticated && pathname !== '/cart' && (newUser || googleOneTapUser) && !dismissedOneTap && isAboveMd &&
                <GoogleOneTap />
              }
            </Container>

            <div className="w-100 nav-line d-none d-lg-block" />

            {!cleanNav &&
              <SecondaryNavBar
                authenticated={authenticated}
                flashSale={flashSale}
                location={location}
                setMegaMenuTab={setMegaMenuTab}
                megaMenuTab={megaMenuTab}
                siteBrand={siteBrand}
                onMegaMenuIntent={loadMegaMenu}
              />
            }
            {shouldLoadMegaMenu &&
              <Suspense fallback={<div />}>
                <MegaMenusContainer
                  setMegaMenuTab={setMegaMenuTab}
                  megaMenuTab={megaMenuTab}
                />
              </Suspense>
            }
          </div>
        </Navbar>


        {isBanned &&
          <div className="prelive-warning-bar">
            <FormattedMessage id="navbar.warning.notabletoprocessspayment" defaultMessage="We are currently not able to process payments from this territory. We apologise for the inconvenience" />
          </div>
        }
        {(showMobile || pathname === '/search') && !notificationCentreOpen &&
          <div className="d-block d-md-none mobile-search-dropdown">
            <AutoSearchWrapper show intialFocus setShowMobile={setShowMobile} />
          </div>
        }

        {!clearNav &&
          <React.Fragment>
            {includes(window.location.href, 'prelive.fanatical.com') &&
              <div className="prelive-warning-bar">
                Warning - This is prelive
              </div>
            }
            {(includes(window.location.href, 'staging.fanatical.com') || includes(window.location.href, 'staging2.fanatical.com')) &&
              <div className="prelive-warning-bar">
                Warning - This is staging
              </div>
            }
            {includes(window.location.href, 'testing.fanatical.com') &&
              <div className="prelive-warning-bar">
                Warning - This is testing
              </div>
            }
            {includes(window.location.href, 'localhost') && env.NodeEnv !== 'production' &&
              <div className="local-warning-bar">
                Dev Mode
                {countryCode && countryCode !== 'GB' &&
                  <span> - {countryName} Preview</span>
                }
              </div>
            }
          </React.Fragment>
        }

        <SlimBannerContainer />
      </div>
    </React.Fragment >
  );
}
